import { PaymentsProvider } from '@zorro/clients';

import { usePaymentsProvider } from '../../hooks/usePaymentsProvider';
import { useZorroPayActivationStatus } from './useZorroPayActivationStatus';

type Props = {
  employerId: string;
  employeeId: string;
};

export type LynxPaymentsContext = {
  /** Whether the lynx_payments_enabled flag is on for this employer. */
  isLynxEnabled: boolean;
  /**
   * The resolved payment provider for this employer.
   * undefined while the feature flag SDK has not yet evaluated — no provider
   * should be assumed and no provider-specific queries should fire.
   */
  paymentsProvider: PaymentsProvider | undefined;
  /**
   * Activation state of the Lynx payment account.
   * undefined = status still loading, true = activated, false = not yet activated.
   */
  isAccountActive?: boolean;
  /**
   * True only when the activation status has resolved and the account is not
   * yet activated. False while loading so no premature alert is shown.
   */
  isWaitingForActivation: boolean;
};

export function useLynxPaymentsContext({
  employerId,
  employeeId,
}: Props): LynxPaymentsContext {
  const { isLynxEnabled, paymentsProvider } = usePaymentsProvider(employerId);

  const { isActivated, employeeAccountStatus } = useZorroPayActivationStatus({
    employeeId,
    enabled: isLynxEnabled,
  });

  const isAccountActive =
    employeeAccountStatus !== undefined ? isActivated : undefined;

  return {
    isLynxEnabled,
    paymentsProvider,
    isAccountActive,
    isWaitingForActivation: isLynxEnabled && isAccountActive === false,
  };
}
